/** @format */

import {
  HeightParams,
  MultilineChartInterface,
} from 'components/charts/multiline-chart/model/common';

import {
  BRUSH_PADDING_HEIGHT,
  CHARTS_GAP,
  MIN_CHART_HEIGHT,
  MIN_CONTAINER_HEIGHT,
  SCATTER_HEIGHT,
  STUB_LAYER_HEIGHT,
  X_AXIS_HEIGHT,
} from 'components/charts/multiline-chart/const';

import {Chart} from 'components/charts/multiline-chart/model/charts';
import {WIDGETS_LIST} from 'pages/Home/DigitalCast/constants/widgets';
import {ChartIds} from 'pages/Home/DigitalCast/models/chartIds';

const defaultHeightParams: HeightParams = {
  overallHeight: MIN_CONTAINER_HEIGHT + BRUSH_PADDING_HEIGHT,
  blockHeight: MIN_CONTAINER_HEIGHT,
  containerHeight: MIN_CONTAINER_HEIGHT - X_AXIS_HEIGHT,
  boxHeight: MIN_CONTAINER_HEIGHT - X_AXIS_HEIGHT,
};

export interface GetHeightParamsReturnResult {
  mappedData: MultilineChartInterface<ChartIds>;
  heightParams: HeightParams;
}

interface Options {
  data: MultilineChartInterface<ChartIds>;
  selectedIds: ChartIds[];
  stubLayerCount?: number;
}

/**
 * Вернет размер для каждого графика (кроме скаттеров), высоту блока, высоту контейнера, высоту коробки.
 * Размеры графиков записываются непосредственно в массив с данными, а все остальное в отдельный объект.
 *
 * Понятия:
 * График: обычный единичный график
 * Скаттер: заметки или логи, у них постоянная высота
 * Коробка: место вывода обычных графиков
 * Контейнер: место вывода всех графиков: коробка + скаттеры
 * Блок: Контейнер + xAxis
 * Общая высота: Виджеты + Блок + BRUSH_PADDING_HEIGHT
 * Дашборд: Общая высота + DASHBOARD_PADDING
 *
 * @param charts
 * @param selectedIds
 */

const getHeightParams = ({
  data,
  selectedIds,
  stubLayerCount = 0,
}: Options): GetHeightParamsReturnResult => {
  const chartsSelected = data.charts.filter(chart => selectedIds.includes(chart.chartId));
  const stubsHeight = stubLayerCount * (STUB_LAYER_HEIGHT + CHARTS_GAP);

  if (!chartsSelected.length) {
    return {
      mappedData: data,
      heightParams: {
        ...defaultHeightParams,
        overallHeight: defaultHeightParams.overallHeight + stubsHeight,
        blockHeight: defaultHeightParams.blockHeight + stubsHeight,
        containerHeight: defaultHeightParams.containerHeight + stubsHeight,
        boxHeight: defaultHeightParams.boxHeight + stubsHeight,
      },
    };
  }

  const chartsNormal = chartsSelected.filter(chart => !chart.isScatter);
  const chartsScatters = chartsSelected.filter(chart => chart.isScatter);

  const chartsWithMinHeight: Chart<ChartIds>[] = [];
  const flexibleCharts: Chart<ChartIds>[] = [];

  chartsNormal.forEach(chart => {
    if (WIDGETS_LIST[chart.chartId].minChartHeight !== undefined) {
      chartsWithMinHeight.push(chart);
    } else {
      flexibleCharts.push(chart);
    }
  });

  const normalChartsCount = chartsNormal.length;
  const scattersChartsCount = chartsScatters.length;

  // Сумма высот всех графиков, у которых указана мин. высота
  const withMinHeightSum = chartsWithMinHeight.reduce(
    (acc, cur) => acc + WIDGETS_LIST[cur.chartId].minChartHeight!,
    0,
  );

  // Сумма высот всех графиков, у которых НЕ указана мин. высота
  const flexibleHeightSum = flexibleCharts.length * MIN_CHART_HEIGHT;

  // Высота всех промежутков
  let normalGapsHeight = normalChartsCount * CHARTS_GAP;

  // Общая высота всех обычных графиков (без промежутков)
  let normalChartsHeight = withMinHeightSum + flexibleHeightSum;

  // Получившаяся высота коробки (включая stub-слои)
  let boxHeight = normalGapsHeight + normalChartsHeight + stubsHeight;

  // Сумма высот всех скаттеров
  const scattersHeightSum = scattersChartsCount * SCATTER_HEIGHT;

  // Высота промежутков скаттеров
  let scattersGapsHeight = scattersChartsCount * CHARTS_GAP;

  // Высота скаттеров
  const scattersBoxHeight = scattersHeightSum + scattersGapsHeight;

  // Высота контейнера
  let containerHeight = boxHeight + scattersBoxHeight;

  // Высота блока
  let blockHeight = containerHeight + X_AXIS_HEIGHT;

  // Высота всего компонента графиков

  let overallHeight = blockHeight + BRUSH_PADDING_HEIGHT;

  // Если containerHeight больше или равен MIN_CONTAINER_HEIGHT, то отправляем результат

  if (containerHeight >= MIN_CONTAINER_HEIGHT) {
    const mappedCharts: Chart<ChartIds>[] = chartsSelected.map(chart => {
      if (chart.isScatter) {
        return chart;
      }
      const id = chart.chartId;
      const newChartHeight = WIDGETS_LIST[id].minChartHeight ?? MIN_CHART_HEIGHT;
      return {
        ...chart,
        chartHeight: newChartHeight,
      };
    });

    return {
      mappedData: {...data, charts: mappedCharts},
      heightParams: {
        overallHeight,
        blockHeight,
        containerHeight,
        boxHeight,
      },
    };
  }

  // Результат получился меньше, чем минимальная высота контейнера. Графики нужно расширить до мин. высоты контейнера

  boxHeight = MIN_CONTAINER_HEIGHT - scattersBoxHeight + stubsHeight;
  normalChartsHeight = boxHeight - normalGapsHeight - stubsHeight;
  let chartHeight = Math.round(normalChartsHeight / normalChartsCount);

  // Собираем все графики, у которых мин. высота оказалась больше, чем chartHeight

  const overflowCharts: Chart<ChartIds>[] = chartsWithMinHeight.filter(chart => {
    const min = WIDGETS_LIST[chart.chartId].minChartHeight!;
    return min > chartHeight;
  });

  // Заново пересчитываем изменившиеся данные

  containerHeight = boxHeight + scattersBoxHeight;
  blockHeight = containerHeight + X_AXIS_HEIGHT;
  overallHeight = blockHeight + BRUSH_PADDING_HEIGHT;

  // Если overflowCharts пуст, то возвращаем результат

  if (!overflowCharts.length) {
    const mappedCharts: Chart<ChartIds>[] = chartsSelected.map(chart => {
      if (chart.isScatter) {
        return chart;
      }
      return {
        ...chart,
        chartHeight,
      };
    });

    return {
      mappedData: {...data, charts: mappedCharts},
      heightParams: {
        overallHeight,
        blockHeight,
        containerHeight,
        boxHeight,
      },
    };
  }

  // Нужно учесть увеличенную высоту для некоторых графиков

  const flexibleChartsCount = flexibleCharts.length;
  const overflowChartsHeight = overflowCharts.reduce((acc, cur) => {
    const min = WIDGETS_LIST[cur.chartId].minChartHeight!;
    return acc + min;
  }, 0);
  const flexibleChartsHeight = normalChartsHeight - overflowChartsHeight;
  const chartHeightQuotient =
    flexibleChartsCount === 0 ? flexibleChartsHeight : flexibleChartsHeight / flexibleChartsCount;
  chartHeight = Math.round(chartHeightQuotient);

  // Возвращаем результат

  const overflowChartsIds = overflowCharts.map(chart => chart.chartId);

  const mappedCharts: Chart<ChartIds>[] = chartsSelected.map(chart => {
    if (chart.isScatter) {
      return chart;
    }
    const isOverflowChart = overflowChartsIds.includes(chart.chartId);
    return {
      ...chart,
      chartHeight: isOverflowChart ? WIDGETS_LIST[chart.chartId].minChartHeight : chartHeight,
    };
  });

  return {
    mappedData: {...data, charts: mappedCharts},
    heightParams: {
      overallHeight,
      blockHeight,
      containerHeight,
      boxHeight,
    },
  };
};

export default getHeightParams;
